import DocumentPage from './DocumentPage';

export const assignmentsDocument = {
  slug: 'assignments',
  route: '/assignments',
  title: 'Assignments',
  category: 'Academics',
  format: 'DOCX',
  description:
    'EduX provides facilities to configure assignment types, create assignments, allocate students, review submissions, and manage assignment workflows from draft through final review states.',
  metrics: [
    { label: 'Primary focus', value: 'Assignment management' },
    { label: 'Core records', value: 'Types, assignments, submissions' },
    { label: 'Lifecycle', value: 'Draft to review states' },
  ],
  workflow: [
    'Configure assignment types from Assignments -> Configuration -> Assignment Types.',
    'Create the assignment with course, batch, subject, faculty, reviewer, dates, marks, and description.',
    'Allocate the assignment to students, publish it, and finish it when the work is completed.',
    'Review student submissions and move them through submitted, accepted, change required, resubmit, or rejected states.',
  ],
  sections: [
    {
      id: 'assignment-types',
      title: 'Assignment Types',
      body: [
        'EduX provides the facility to configure the assignment type. List of all the assignment type are displayed here.',
        'Go to Assignments -> Configuration -> Assignment Types.',
        'The below image shows the assignment type.',
      ],
      figure: {
        src: '/assets/doc-images/assignments/image1.png',
        alt: 'Assignment type record showing name, code, and type',
        caption:
          'Assignment Type form showing the assignment type name, code, and type fields.',
      },
      detailCards: [
        {
          title: 'Details of Assignment Types',
          bullets: [
            'Name: specify the name of the assignment type.',
            'Code: you can also provide code of assignment type.',
            'Type: specify the type of assignment being created.',
          ],
        },
      ],
    },
    {
      id: 'assignments',
      title: 'Assignments',
      body: [
        'EduX provides the facility to create the assignments. All the assignments are displayed here.',
        'Go to Assignments -> Assignments -> Assignments.',
        'The below image shows the assignment detail.',
      ],
      figure: {
        src: '/assets/doc-images/assignments/image2.png',
        alt: 'Assignment form showing course, batch, subject, faculty, reviewer, and tabs',
        caption:
          'Assignment form showing the assignment header, faculty and reviewer details, state bar, and the Basic Information, Allocation Information, and Submissions tabs.',
      },
      detailCards: [
        {
          title: 'Assignment Details',
          bullets: [
            'Name: specify the name of the assignment.',
            'Course: select the course from the list for which assignment is created.',
            'Batch: based on the selected course list of batch appears, select appropriate batch from the list.',
            'Subject: select the subject from the list whose assignment is created.',
            'Faculty: select the faculty who has created the assignment from the Faculty list.',
            'Assignment Type: specify the type of assignment being created.',
            'Reviewer: specify the name of the person from the Reviewer list who will review the assignment.',
            'Submission (Smart Button): list of all the submitted assignments by the students appears here.',
          ],
        },
      ],
      subsections: [
        {
          id: 'basic-information',
          title: 'Basic Information',
          body: [
            'The Basic Information page captures the assignment timeline, marks, and description for the assignment record.',
          ],
          bullets: [
            "Issued Date: Issued Date is the date on which assignment is issued. By default today's date and time is set here.",
            'Submission Date: Submission Date is the date on which assignment is to be submitted.',
            'Marks: allocated the marks for the assignment created.',
            'Description: specify the details of assignment in description field.',
          ],
        },
        {
          id: 'allocation-information',
          title: 'Allocation Information',
          body: [
            'Specify the list of students to whom the assignment is to be allocated, i.e. all the students who will perform the assignment.',
          ],
        },
        {
          id: 'assignment-submissions-tab',
          title: 'Submissions',
          body: [
            'Click on the Submissions button and show list of all the submitted assignments by the students appears here.',
          ],
          figure: {
            src: '/assets/doc-images/assignments/image3.png',
            alt: 'Submissions tab inside the assignment form',
            caption:
              'Submissions tab inside the assignment form showing linked student submissions with submission date, state, marks, and note fields.',
          },
        },
      ],
    },
    {
      id: 'assignment-workflow',
      title: 'Workflow of Assignments',
      body: [
        'When assignment is created it is in Draft state.',
      ],
      figure: {
        src: '/assets/doc-images/assignments/image4.png',
        alt: 'Published assignment showing publish and finish workflow states',
        caption:
          'Assignment record showing the workflow actions and the state bar moving from Draft to Published to Finished.',
      },
      subsections: [
        {
          id: 'assignment-draft-state',
          title: 'Draft',
          body: ['When assignment is created it starts in Draft state.'],
        },
        {
          id: 'assignment-published-state',
          title: 'Published',
          body: [
            'Click on the Publish button to publish the assignment. It changes the state from Draft to Published.',
          ],
        },
        {
          id: 'assignment-finished-state',
          title: 'Finished',
          body: [
            'Click on Finish button if the assignment is completed. It changes the state from Published to Finished.',
          ],
        },
      ],
    },
    {
      id: 'assignment-submissions',
      title: 'Assignment Submissions',
      body: [
        'EduX provides the Assignment Submission facility. List of all the assignments submitted by the students are displayed here.',
        'Go to Assignments -> Assignments -> Submissions.',
        'The below image shows the submitted assignments.',
      ],
      figure: {
        src: '/assets/doc-images/assignments/image5.png',
        alt: 'Assignment submissions list showing assignment, student, date, and state',
        caption:
          'Assignment Submissions list showing the assignment name, student, submission date, and submission state.',
      },
      detailCards: [
        {
          title: 'Details of Assignment Submissions',
          bullets: [
            'Student: specify the student whose assignment is to be submitted.',
            'Assignment: select the name of the assignment from the list of available assignments or create a new assignment.',
            'Submission Date: by default todays date and time is set in the Submission Date.',
            'Description and Note: you can also provide description and note about the assignment.',
          ],
        },
      ],
    },
    {
      id: 'submission-workflow',
      title: 'Workflow of Assignment Submissions',
      body: [
        'Assignment submissions move through review states based on whether the work is accepted, requires changes, or is rejected.',
      ],
      subsections: [
        {
          id: 'submission-draft-state',
          title: 'Draft',
          body: ['When assignment is created, it is in Draft state.'],
        },
        {
          id: 'submission-submitted-state',
          title: 'Submitted',
          body: [
            'Click on the Submit button to submit the assignment. It changes the state from Draft to Submitted.',
          ],
          figure: {
            src: '/assets/doc-images/assignments/image6.png',
            alt: 'Submitted assignment submission with accept, change require, and reject actions',
            caption:
              'Assignment submission record in Submitted state showing the Accept, Change Require, and Reject actions.',
          },
        },
        {
          id: 'submission-accepted-state',
          title: 'Accepted',
          body: [
            'Click on Accept button if the assignment is correct. It changes the state from Submitted to Accepted.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/assignments/image7.png',
              alt: 'Assignment submission state bar in Accepted state',
              caption: 'State bar showing the submission moved to Accepted.',
            },
          ],
        },
        {
          id: 'submission-change-required-state',
          title: 'Change Required',
          body: [
            'If some changes are required in the assignment then click on Change Require button. It changes the state from Submitted to Change Req.',
            'After changes click on Change and Resubmit to submit the assignment after the changes. It changes the state from Change Req. to Draft.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/assignments/image8.png',
              alt: 'Submitted assignment submission before selecting Change Require',
              caption: 'Submitted assignment submission ready for a Change Require review action.',
            },
            {
              src: '/assets/doc-images/assignments/image9.png',
              alt: 'Assignment submission in Change Required state with Change And Resubmit action',
              caption:
                'Submission in Change Required state showing the Change And Resubmit action.',
            },
          ],
        },
        {
          id: 'submission-rejected-state',
          title: 'Reject',
          body: [
            'You can also reject the assignment by clicking the Reject button. It changes to Rejected state.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/assignments/imagea.png',
              alt: 'Submitted assignment submission showing the Reject action',
              caption: 'Submitted assignment submission showing the Reject action.',
            },
            {
              src: '/assets/doc-images/assignments/imageb.png',
              alt: 'Assignment submission in Rejected state',
              caption: 'State bar showing the submission moved to Rejected.',
            },
          ],
        },
      ],
    },
  ],
  related: ['faculty', 'student', 'exam'],
};

export default function AssignmentsPage() {
  return <DocumentPage document={assignmentsDocument} />;
}
